import { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, BadgeCheck } from "lucide-react";
import { Eyebrow, scrollToId } from "./Reveal";
import { useLang } from "@/i18n/LangContext";

const ease = [0.16, 1, 0.3, 1];

const BreathPill = () => {
  const { t } = useLang();
  const phases = t("hero.breath");
  const [i, setI] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setI((p) => (p + 1) % phases.length), 4000);
    return () => clearInterval(t);
  }, [phases.length]);
  return (
    <div
      data-testid="interactive-breath-counter"
      className="inline-flex items-center gap-3 rounded-full border px-5 py-2.5"
      style={{ borderColor: "var(--border-delicate)", backgroundColor: "var(--bg-glass)" }}
    >
      <span className="breath-dot block h-2.5 w-2.5 rounded-full" style={{ backgroundColor: "var(--accent-gold)" }} />
      <span className="font-mono-alt text-xs uppercase tracking-[0.3em]" style={{ color: "var(--text-secondary)" }}>
        {phases[i]} · 4s
      </span>
    </div>
  );
};

export default function Hero() {
  const { lang, t } = useLang();
  const LINES = t("hero.lines");
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const haloY = useTransform(scrollYProgress, [0, 1], [0, -60]);

  return (
    <section
      id="hero"
      ref={ref}
      data-testid="hero-section"
      className="relative overflow-hidden px-5 pt-32 pb-20 sm:px-8 lg:pt-40 lg:pb-28"
    >
      <div
        className="pointer-events-none absolute -top-40 right-[-10%] h-[34rem] w-[34rem] rounded-full blur-3xl"
        style={{ background: "var(--accent-glow)" }}
      />
      <div className="mx-auto grid max-w-7xl items-center gap-16 lg:grid-cols-[1.15fr_0.85fr]">
        <div>
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease }}>
            <Eyebrow>{t("hero.eyebrow")}</Eyebrow>
          </motion.div>

          <h1
            key={lang}
            data-testid="hero-main-title"
            className="font-display mt-7 text-4xl font-light leading-[1.08] tracking-tight sm:text-5xl lg:text-7xl"
          >
            {LINES.map((line, i) => (
              <span key={i} className="block overflow-hidden pb-1">
                <motion.span
                  className="block"
                  initial={{ y: "110%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 1.1, delay: 0.35 + i * 0.15, ease }}
                  style={{ fontStyle: i >= 2 ? "italic" : "normal", color: i >= 2 ? "var(--accent-gold)" : "var(--text-primary)" }}
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            className="mt-7 max-w-xl text-base font-light leading-relaxed sm:text-lg"
            style={{ color: "var(--text-secondary)" }}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.1, ease }}
          >
            {t("hero.sub")}
          </motion.p>

          <motion.div
            className="mt-9 flex flex-wrap items-center gap-4"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.3, ease }}
          >
            <button
              data-testid="hero-cta-explore-button"
              onClick={() => scrollToId("meditations")}
              className="group flex items-center gap-2 rounded-full px-7 py-3.5 text-sm font-semibold tracking-wide transition-transform hover:scale-[1.04]"
              style={{ backgroundColor: "var(--primary-btn-bg)", color: "var(--primary-btn-text)" }}
            >
              {t("hero.ctaExplore")}
              <ArrowDown size={15} className="transition-transform group-hover:translate-y-0.5" />
            </button>
            <button
              data-testid="hero-cta-invite-button"
              onClick={() => scrollToId("contact")}
              className="rounded-full border px-7 py-3.5 text-sm font-medium tracking-wide transition-colors"
              style={{ borderColor: "var(--border-hover)", color: "var(--text-primary)" }}
            >
              {t("hero.ctaInvite")}
            </button>
            <div className="mt-2 w-full sm:mt-0 sm:w-auto">
              <BreathPill />
            </div>
          </motion.div>
        </div>

        <div className="relative mx-auto w-full max-w-sm lg:max-w-md">
          <motion.div
            className="aura-ring absolute inset-[-8%] rounded-full blur-2xl"
            style={{ background: "var(--accent-glow)", y: haloY }}
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.92 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.2, delay: 0.5, ease }}
            className="relative"
            style={{ y: imgY }}
          >
            <div
              className="overflow-hidden rounded-t-[999px] rounded-b-[2rem] border p-2"
              style={{ borderColor: "var(--border-hover)", backgroundColor: "var(--bg-surface)" }}
            >
              <img
                data-testid="hero-mahima-portrait"
                src="/images/mahima.webp"
                alt="Maa Prem Mahima - Osho Meditation Facilitator"
                className="aspect-[4/5] w-full rounded-t-[999px] rounded-b-[1.6rem] object-cover object-top"
              />
            </div>
            <div
              className="absolute -bottom-4 left-1/2 flex -translate-x-1/2 items-center gap-2 whitespace-nowrap rounded-full border px-4 py-2"
              style={{ borderColor: "var(--border-delicate)", backgroundColor: "var(--bg-glass)", backdropFilter: "blur(12px)" }}
            >
              <BadgeCheck size={15} style={{ color: "var(--accent-gold)" }} />
              <span className="font-mono-alt text-[10px] uppercase tracking-[0.25em]" style={{ color: "var(--text-secondary)" }}>
                {t("hero.badge")}
              </span>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
